import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';

import { Box } from '@mui/material';

import { AG_GRID_STYLE_OVERRIDES, COLUMN_TYPES } from '../AGTable/AGTable.utils';
import { ensureAgGridSetup } from '../AGTable/ag-grid-setup';

// Lazily register AG Grid enterprise modules on first grid render
ensureAgGridSetup();

const AGServerTable = ({
  sx,
  rowData,
  colDefs,
  rowHeight = 42,
  rowBuffer = 60,
  cacheSize = 500,
  cacheBlocks = 10,
  isSelectable = false,
  isMoveable = true,
  isEditable = false,
  isFilterFloating = false,
  isSidebarVisible = false,
  isGroupingVisible = false,
  isAutoResize = false,
  pagination,
  defaultPagination = 50,
  contextMenuItems = null,
  sideBarItems = null,
  onReady,
  onRowClick,
  onUpdate,
  onSelect,
  rowSelection = null,
  ...rest
}) => {
  // AG Grid needs the columns to be stored in state
  const [AGcolDefs, setAGcolDefs] = useState(colDefs);
  useEffect(() => {
    // Update stored column definitions
    if (colDefs) {
      setAGcolDefs(colDefs);
    }
  }, [colDefs]);

  // Default column settings
  const defaultColDef = useMemo(() => {
    const contextItems = contextMenuItems || [];
    const colDef = {
      type: 'string',
      minWidth: 72,
      suppressMovable: !isMoveable,
      floatingFilter: isFilterFloating,
      filterParams: {
        maxNumConditions: 10,
        buttons: ['clear'],
      },
      contextMenuItems: [...contextItems, 'copy', 'export'],
    };

    // Bypass the config's context menu when the function getter is provided
    if (rest.getContextMenuItems) {
      delete colDef.contextMenuItems;
    }

    return colDef;
  }, [isMoveable, isFilterFloating, contextMenuItems]);

  // Side bar settings
  const sideBarDef = useMemo(() => {
    const sideItems = sideBarItems || [];
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressExpandAll: false,
            suppressFilterSearch: false,
          },
        },
        ...sideItems,
      ],
      defaultToolPanel: 'columns',
    };
  }, [sideBarItems]);

  // Row selection settings
  const rowSelectionDefault = useMemo(() => {
    return isSelectable
      ? {
          mode: 'multiRow',
          selectAll: 'filtered',
          enableClickSelection: false,
        }
      : { checkboxes: false, headerCheckbox: false, enableClickSelection: false };
  }, [isSelectable]);

  return (
    <Box
      sx={{
        ...AG_GRID_STYLE_OVERRIDES,
        ...sx,
      }}
    >
      <AgGridReact
        rowModelType="serverSide"
        editType="fullRow"
        columnTypes={COLUMN_TYPES}
        columnDefs={AGcolDefs}
        suppressClickEdit={!isEditable}
        pagination={Boolean(pagination?.length)}
        paginationPageSize={defaultPagination}
        paginationPageSizeSelector={pagination}
        sideBar={isSidebarVisible ? sideBarDef : {}}
        rowGroupPanelShow={isGroupingVisible ? 'always' : ''}
        autoSizeStrategy={isAutoResize ? { type: 'fitCellContents' } : undefined}
        cacheBlockSize={cacheSize}
        maxBlocksInCache={cacheBlocks}
        onGridReady={onReady}
        onRowClicked={(e) => onRowClick?.(e.rowIndex)}
        onModelUpdated={(e) => onUpdate?.(e.api)}
        onSelectionChanged={(e) => onSelect?.(e.api.getSelectedRows())}
        rowSelection={{ ...rowSelectionDefault, ...(rowSelection || {}) }}
        {...{ defaultColDef, rowHeight, rowBuffer, rowData }}
        {...rest}
      />
    </Box>
  );
};

AGServerTable.propTypes = {
  sx: PropTypes.object,
  rowData: PropTypes.any,
  colDefs: PropTypes.any,
  rowHeight: PropTypes.number,
  rowBuffer: PropTypes.number,
  cacheSize: PropTypes.number,
  cacheBlocks: PropTypes.number,
  isMoveable: PropTypes.bool,
  isEditable: PropTypes.bool,
  isSelectable: PropTypes.bool,
  isSidebarVisible: PropTypes.bool,
  isGroupingVisible: PropTypes.bool,
  isAutoResize: PropTypes.bool,
  pagination: PropTypes.arrayOf(PropTypes.number),
  defaultPagination: PropTypes.number,
  contextMenuItems: PropTypes.any,
  sideBarItems: PropTypes.any,
  serverDataSource: PropTypes.any,
  onReady: PropTypes.func,
  onRowClick: PropTypes.func,
  onUpdate: PropTypes.func,
  onSelect: PropTypes.func,
  rowSelection: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default AGServerTable;
